import { cn } from "@/lib/utils";
import { CalendarIcon } from "@phosphor-icons/react"; // Add this import
import {
  DatePicker as AriaDatePicker,
  type DatePickerProps as AriaDatePickerProps,
  type DateValue as AriaDateValue,
  Dialog as AriaDialog,
  type DialogProps as AriaDialogProps,
  type PopoverProps as AriaPopoverProps,
  type ValidationResult as AriaValidationResult,
  Text,
  composeRenderProps,
} from "react-aria-components";
import { Button } from "./button";
import {
  Calendar,
  CalendarCell,
  CalendarGrid,
  CalendarGridBody,
  CalendarGridHeader,
  CalendarHeaderCell,
  CalendarHeading,
  // RangeCalendar,
} from "./calendar";
import { DateInput } from "./datefield";
import { FieldError, FieldGroup, Label } from "./field";
import { Popover } from "./popover";

const DatePicker = AriaDatePicker;

// const DateRangePicker = AriaDateRangePicker;

const DatePickerContent = ({
  className,
  popoverClassName,
  ...props
}: AriaDialogProps & { popoverClassName?: AriaPopoverProps["className"] }) => (
  <Popover
    className={composeRenderProps(popoverClassName, (className) =>
      cn("w-auto p-3 bg-white", className),
    )}
  >
    <AriaDialog
      className={cn(
        "flex w-full flex-col space-y-4 outline-hidden sm:flex-row sm:space-x-4 sm:space-y-0",
        className,
      )}
      {...props}
    />
  </Popover>
);

interface JollyDatePickerProps<T extends AriaDateValue>
  extends AriaDatePickerProps<T> {
  label?: string;
  description?: string;
  errorMessage?: string | ((validation: AriaValidationResult) => string);
  size?: "sm" | "md" | "lg";
}

function JollyDatePicker<T extends AriaDateValue>({
  label,
  description,
  errorMessage,
  className,
  size = "sm",
  ...props
}: JollyDatePickerProps<T>) {
  return (
    <DatePicker
      className={cn(
        "group flex flex-col",
        {
          "text-xs": size === "sm",
          "text-sm": size === "md",
          "text-base": size === "lg",
        },
        className,
      )}
      {...props}
    >
      {label && (
        <Label
          className={cn({
            "text-xs": size === "sm",
            "text-sm": size === "md",
            "text-base": size === "lg",
          })}
        >
          {label}
        </Label>
      )}
      <FieldGroup>
        <DateInput
          className={cn(
            "flex-1 border-0 px-3 py-2 text-sm",
            size === "sm" && "text-xs",
          )}
          variant="ghost"
        />
        <Button
          variant="ghost"
          size="icon"
          className="mr-1 size-6 data-focus-visible:ring-offset-0"
        >
          <CalendarIcon className="size-4 text-gray-700" />
        </Button>
      </FieldGroup>
      {description && (
        <Text
          className="text-sm text-slate-500 dark:text-slate-400"
          slot="description"
        >
          {description}
        </Text>
      )}
      <FieldError>{errorMessage}</FieldError>
      <DatePickerContent>
        <Calendar>
          <CalendarHeading />
          <CalendarGrid>
            <CalendarGridHeader>
              {(day) => <CalendarHeaderCell>{day}</CalendarHeaderCell>}
            </CalendarGridHeader>
            <CalendarGridBody>
              {(date) => <CalendarCell date={date} />}
            </CalendarGridBody>
          </CalendarGrid>
        </Calendar>
      </DatePickerContent>
    </DatePicker>
  );
}

export { JollyDatePicker };
export type { JollyDatePickerProps };
